'use client';

import type { ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';
import { Tooltip } from './Tooltip';

type DetailTopBarIconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'children'> & {
  icon:
    | 'archive'
    | 'complete'
    | 'convert'
    | 'delete'
    | 'download'
    | 'edit'
    | 'resolve'
    | 'share'
    | 'star'
    | 'star-filled'
    | 'unarchive';
  label: string;
};

export type DetailTopBarIconName = DetailTopBarIconButtonProps['icon'];

export const DetailTopBarIcon = ({ icon }: { icon: DetailTopBarIconName }) => {
  const commonProps = {
    xmlns: 'http://www.w3.org/2000/svg',
    className: 'h-4 w-4',
    fill: 'none',
    viewBox: '0 0 24 24',
    stroke: 'currentColor',
    'aria-hidden': true,
  };

  if (icon === 'edit') {
    return (
      <svg {...commonProps}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
        />
      </svg>
    );
  }

  if (icon === 'delete') {
    return (
      <svg {...commonProps}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
        />
      </svg>
    );
  }

  if (icon === 'complete') {
    return (
      <svg {...commonProps}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
      </svg>
    );
  }

  if (icon === 'download') {
    return (
      <svg {...commonProps}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
        />
      </svg>
    );
  }

  if (icon === 'archive') {
    return (
      <svg {...commonProps}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M4 4h16v4H4V4zM6 8v11a1 1 0 001 1h10a1 1 0 001-1V8M10 12h4"
        />
      </svg>
    );
  }

  if (icon === 'convert') {
    return (
      <svg {...commonProps}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M4 7h11m0 0l-3-3m3 3l-3 3M20 17H9m0 0l3-3m-3 3l3 3"
        />
      </svg>
    );
  }

  if (icon === 'star' || icon === 'star-filled') {
    // 별표: 켜짐(star-filled)은 채워서, 꺼짐(star)은 외곽선으로 렌더한다.
    return (
      <svg {...commonProps} fill={icon === 'star-filled' ? 'currentColor' : 'none'}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
        />
      </svg>
    );
  }

  if (icon === 'share') {
    return (
      <svg {...commonProps}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
        />
      </svg>
    );
  }

  if (icon === 'unarchive') {
    return (
      <svg {...commonProps}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M4 4h16v4H4V4zM6 8v11a1 1 0 001 1h10a1 1 0 001-1V8M12 17v-5m0 0l-2 2m2-2l2 2"
        />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
};

export const DetailTopBarIconButton = ({
  icon,
  label,
  className,
  type = 'button',
  ...buttonProps
}: DetailTopBarIconButtonProps) => {
  return (
    <Tooltip content={label} placement="bottom" className="inline-flex shrink-0">
      <button
        {...buttonProps}
        type={type}
        aria-label={label}
        className={cn('btn btn-square btn-ghost btn-sm shrink-0', className)}
      >
        <DetailTopBarIcon icon={icon} />
      </button>
    </Tooltip>
  );
};
